import { FetchArticlesFilterParams, FetchArticlesParams } from '../services/articles/articles.models';
import { FeedType, SnippetParams, TopReadsQueryParams } from '../models';
import { DealsParams } from '../services/deals/deals.service';
import { ArticleDto } from '../services/dtos/article/article.dto';
import { SearchFeedRequestBodyDto } from '../services/dtos/article/searchFeedRequestBody.dto';
import { ReadingListDto } from '../services/dtos/readingList/readingList.dto';
import { CreditRiskLevelMetric } from '../services/dtos/riskProducts/creditRiskLevels.dto';
import { RiskDistributionGroupBy, RiskDistributionMetric } from '../services/dtos/riskProducts/riskDistribution.dto';
import { IssuersQueryParams } from '../services/issuers/issuersQueryParams';
import { CaseDocumentsQueryFilters } from './dockets/useFetchCaseDocuments';
import { SearchBondsPayload } from './documents/useFetchBonds';
import { SearchLoansPayload } from './documents/useFetchLoans';
import { SearchContentType } from './searches/useFetchSearch';

export const queryKeys = {
  fetchIssuers: {
    all: 'fetchIssuers' as const,
    single: (tag: string) => [queryKeys.fetchIssuers.all, tag] as const,
    query: (queryParams: IssuersQueryParams) => [queryKeys.fetchIssuers.all, queryParams] as const,
    components: {
      all: (tag: string) => [...queryKeys.fetchIssuers.single(tag), 'components'] as const,
      single: (tag: string, componentTag: string) =>
        [...queryKeys.fetchIssuers.components.all(tag), componentTag] as const,
    },
  },
  fetchArticles: {
    all: 'fetchArticles' as const,
    singleById: (articleId: number | null) => [queryKeys.fetchArticles.all, articleId] as const,
    content: (article: ArticleDto | null) => [queryKeys.fetchArticles.all, article] as const,
    feed: (feedType: FeedType) => [queryKeys.fetchArticles.all, feedType] as const,
    feedByParams: (params: FetchArticlesParams) => [queryKeys.fetchArticles.all, params] as const,
  },
  fetchPaginatedArticles: {
    all: 'fetchPaginatedArticles' as const,
    feed: (feedType: FeedType) => [queryKeys.fetchPaginatedArticles.all, feedType] as const,
    paginatedArticles: (feedType: FeedType, filters: FetchArticlesFilterParams | null, page: number, take: number) =>
      [...queryKeys.fetchPaginatedArticles.feed(feedType), filters, page, take] as const,
  },
  fetchWatchlists: {
    all: 'fetchWatchlists' as const,
  },
  fetchSearches: {
    all: 'fetchSearches',
    allByContentType: (contentType: SearchContentType, filters: SearchFeedRequestBodyDto, page: number) =>
      [queryKeys.fetchSearches.all, contentType, filters, page] as const,
  },
  fetchRecentlyViewed: {
    all: 'fetchRecentlyViewed' as const,
  },
  fetchReadingLists: {
    all: 'fetchReadingList' as const,
    defaultReadingList: (readingListName: string, readingLists: ReadingListDto[] | undefined) =>
      ['fetchDefaultReadingList', readingListName, readingLists] as const,
    defaultReadingListWithTags: (readingList: ReadingListDto | undefined) =>
      ['fetchDefaultReadingListWithTags', readingList] as const,
  },
  fetchMyContentPreferences: 'fetchMyContentPreferences' as const,
  fetchTopReads: {
    all: 'fetchTopReads' as const,
    query: (queryParams: TopReadsQueryParams) => [queryKeys.fetchTopReads.all, queryParams] as const,
  },
  fetchCompanyDeals: {
    all: 'fetchCompanyDeals' as const,
    allByFilters: (companyId: string, params: DealsParams) =>
      [queryKeys.fetchCompanyDeals.all, companyId, params] as const,
  },
  fetchAllAnalysts: 'fetchAllAnalysts' as const,
  fetchLikes: {
    all: 'fetchLikes' as const,
  },
  fetchContentTypePermissions: 'fetchContentTypePermissions' as const,
  fetchSavedSearches: {
    all: 'fetchSavedSearches',
  },
  fetchProfile: 'fetchProfile' as const,
  fetchBonds: {
    allByIssuer: (companyId: string, payload: SearchBondsPayload, enabled: boolean) =>
      ['fetchBonds', companyId, payload, enabled] as const,
  },
  fetchLoans: {
    allByIssuer: (companyId: string, payload: SearchLoansPayload, enabled: boolean) =>
      ['fetchLoans', companyId, payload, enabled] as const,
  },
  dockets: {
    fetchCases: {
      all: 'fetchCases' as const,
      allByIssuer: (companyId: string) => [queryKeys.dockets.fetchCases.all, companyId] as const,
    },
    fetchDocuments: {
      all: 'fetchDocuments' as const,
      query: (
        docketCaseId: number,
        page: number,
        pageSize: number,
        filters: CaseDocumentsQueryFilters,
        keyFilings: boolean
      ) => [queryKeys.dockets.fetchDocuments.all, docketCaseId, page, pageSize, filters, keyFilings] as const,
      // Query key for invalidation skips filters to invalidate both filtered and unfiltered caches
      queryForInvalidation: (docketCaseId: number, page: number, pageSize: number) =>
        [queryKeys.dockets.fetchDocuments.all, docketCaseId, page, pageSize] as const,
      attachments: (
        docketCaseId: number,
        docketItemNumber: number,
        date: string,
        pageSize: number,
        filters: CaseDocumentsQueryFilters,
        keyFilings: boolean
      ) =>
        [
          queryKeys.dockets.fetchDocuments.all,
          docketCaseId,
          docketItemNumber,
          date,
          pageSize,
          filters,
          keyFilings,
        ] as const,
      // Query key for invalidation skips filters to invalidate both filtered and unfiltered caches
      attachmentsForInvalidation: (docketCaseId: number, docketItemNumber: number, date: string, pageSize: number) =>
        [queryKeys.dockets.fetchDocuments.all, docketCaseId, docketItemNumber, date, pageSize] as const,
    },
  },
  fetchSnippets: {
    all: 'fetchSnippets' as const,
    allByFilters: (params: SnippetParams) => [queryKeys.fetchSnippets.all, params] as const,
    single: (type: string, id: string) => [queryKeys.fetchSnippets.all, type, id] as const,
  },
  fetchAnnouncements: 'fetchAnnouncements' as const,
  fetchFinancials: {
    allByIssuer: (companyId: string) => ['fetchFinancials', companyId] as const,
  },
  fetchRiskProducts: {
    overview: (companyId: string) => ['riskOverview', companyId] as const,
    chartByCompany: (companyId: string) => ['fetchCompanyChart', companyId] as const,
    fundamentals: (companyId: string) => ['fundamentals', companyId] as const,
    columns: ['rpColumns'] as const,
    dashboard: {
      byWatchList: (watchListId: string) => ['dashboardByWatchList', watchListId] as const,
      monitor: (watchListId: string) =>
        [queryKeys.fetchRiskProducts.dashboard.byWatchList(watchListId), 'monitor'] as const,
      creditRiskLevel: (watchListId: string, metric: CreditRiskLevelMetric, rated = true) =>
        [
          queryKeys.fetchRiskProducts.dashboard.byWatchList(watchListId),
          'creditRiskLevel',
          metric,
          String(rated),
        ] as const,
      riskDistribution: (
        watchListId: string,
        groupBy: RiskDistributionGroupBy,
        metric?: RiskDistributionMetric,
        rated = true
      ) =>
        [
          queryKeys.fetchRiskProducts.dashboard.byWatchList(watchListId),
          'riskDistribution',
          groupBy,
          metric,
          String(rated),
        ] as const,
      changeCharts: (watchListId: string, groupBy: RiskDistributionGroupBy, rated = true) =>
        [
          queryKeys.fetchRiskProducts.dashboard.byWatchList(watchListId),
          'changeCharts',
          groupBy,
          String(rated),
        ] as const,
      recentChanges: (watchListId: string) =>
        [queryKeys.fetchRiskProducts.dashboard.byWatchList(watchListId), 'recentChanges'] as const,
      drillDown: (companies: number[]) => ['drillDown', ...companies] as const,
      widgetMetrics: ['widgetMetrics'] as const,
    },
  },
  fetchUrlMapping: {
    single: (url: string) => ['fetchUrlMapping', url] as const,
  },
};
